import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download, RefreshCw } from "lucide-react";

type Lead = {
  id: string; full_name: string | null; phone: string; interest: string; landing_path: string | null;
  utm_source: string | null; utm_campaign: string | null; created_at: string;
};

export default function LeadsManagement() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    const { data } = await supabase.from("leads" as any).select("*").order("created_at", { ascending: false }).limit(1000);
    setLeads((data as any) || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => leads.filter((l) =>
    `${l.full_name ?? ""} ${l.phone} ${l.utm_campaign ?? ""} ${l.utm_source ?? ""}`.toLowerCase().includes(q.toLowerCase())), [leads, q]);

  const exportCsv = () => {
    const rows = [["Name", "Phone", "Interest", "Landing page", "Source", "Campaign", "Date"],
      ...filtered.map((l) => [l.full_name ?? "", l.phone, l.interest, l.landing_path ?? "", l.utm_source ?? "", l.utm_campaign ?? "", new Date(l.created_at).toISOString()])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const today = leads.filter((l) => new Date(l.created_at).toDateString() === new Date().toDateString()).length;

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle>Potential Customers ({leads.length}) <span className="text-sm font-normal text-muted-foreground">· {today} today</span></CardTitle>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load}><RefreshCw className="h-4 w-4 mr-1" />Refresh</Button>
          <Button size="sm" onClick={exportCsv} disabled={!filtered.length}><Download className="h-4 w-4 mr-1" />Export CSV</Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <Input placeholder="Search name, phone, campaign..." value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Name</TableHead><TableHead>Phone</TableHead><TableHead>Interest</TableHead>
              <TableHead>Landing page</TableHead><TableHead>Source / Campaign</TableHead><TableHead>Date</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading ? <TableRow><TableCell colSpan={6}>Loading...</TableCell></TableRow> :
                !filtered.length ? <TableRow><TableCell colSpan={6} className="text-muted-foreground">No leads yet.</TableCell></TableRow> :
                filtered.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell>{l.full_name || "—"}</TableCell>
                    <TableCell><a className="text-primary underline" href={`https://wa.me/${l.phone.replace("+", "")}`} target="_blank" rel="noreferrer">{l.phone}</a></TableCell>
                    <TableCell><Badge variant="secondary">{l.interest}</Badge></TableCell>
                    <TableCell className="text-xs">{l.landing_path || "—"}</TableCell>
                    <TableCell className="text-xs">{[l.utm_source, l.utm_campaign].filter(Boolean).join(" / ") || "Direct"}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
